import {
  computed,
  getCurrentInstance,
  getCurrentScope,
  inject,
  nextTick,
  onScopeDispose,
  ref,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';
import { nxAnnounce } from '../Announce/announcer';
import { i18nInjectionKey } from '../internal';
import {
  clampIndex,
  sortableDropSide,
  sortableTargetIndex,
  type NxSortableDropSide,
  type NxSortableSpan,
} from './sortMath';

export type NxSortableAxis = 'vertical' | 'horizontal';

/** Screen-reader texts; `position` is 1-based. German defaults. */
export interface NxSortableMessages {
  /**
   * Role description of a grip (`aria-roledescription`). Optional, so a
   * caller's own complete set of texts from before it existed stays valid.
   */
  grip?: () => string;
  /** Accessible name of a grip. */
  handle: (label: string) => string;
  lifted: (label: string, position: number, count: number) => string;
  moved: (label: string, position: number, count: number) => string;
  dropped: (label: string, position: number, count: number) => string;
  cancelled: (label: string, position: number, count: number) => string;
}

export const nxSortableDefaultMessages: Required<NxSortableMessages> = {
  grip: () => 'Ziehgriff',
  handle: (label) => `${label} verschieben`,
  lifted: (label, position, count) =>
    `${label} aufgenommen, Position ${position} von ${count}. Pfeiltasten verschieben, Leertaste legt ab, Escape bricht ab.`,
  moved: (label, position, count) => `${label}: Position ${position} von ${count}.`,
  dropped: (label, position, count) => `${label} abgelegt auf Position ${position} von ${count}.`,
  cancelled: (label, position, count) =>
    `Verschieben abgebrochen, ${label} bleibt auf Position ${position} von ${count}.`,
};

/**
 * The texts in the app's language: the kit's own (`common.sortable.*`) where
 * the app provides the kit's tables, the German defaults otherwise; `overrides`
 * win. Call it in a component's setup (it injects the kit's i18n); elsewhere
 * it gives the defaults.
 */
export function useNxSortableMessages(
  overrides?: Partial<NxSortableMessages>
): Required<NxSortableMessages> {
  const i18n = getCurrentInstance() ? inject(i18nInjectionKey, null) : null;
  const tr = (key: keyof NxSortableMessages, params: Record<string, unknown>, fallback: string) => {
    const full = `common.sortable.${key}`;
    const text = i18n?.t(full, params);
    return text && text !== full ? text : fallback;
  };
  const d = nxSortableDefaultMessages;
  return {
    grip: () => tr('grip', {}, d.grip()),
    handle: (label) => tr('handle', { label }, d.handle(label)),
    lifted: (label, position, count) =>
      tr('lifted', { label, position, count }, d.lifted(label, position, count)),
    moved: (label, position, count) =>
      tr('moved', { label, position, count }, d.moved(label, position, count)),
    dropped: (label, position, count) =>
      tr('dropped', { label, position, count }, d.dropped(label, position, count)),
    cancelled: (label, position, count) =>
      tr('cancelled', { label, position, count }, d.cancelled(label, position, count)),
    ...overrides,
  };
}

export interface NxSortableOptions<T> {
  items: MaybeRefOrGetter<readonly T[]>;
  /** The element that holds the items (their `itemProps` must be spread inside it). */
  container: Ref<HTMLElement | null | undefined>;
  /** What auto-scrolls near its edges. Default: the nearest scrollable ancestor of the items. */
  scrollContainer?: Ref<HTMLElement | null | undefined>;
  /** Drag axis. Default `'vertical'`. */
  axis?: NxSortableAxis;
  disabled?: MaybeRefOrGetter<boolean>;
  /** Spoken name of an item. Default `Eintrag <n>`. */
  labelOf?: (item: T, index: number) => string;
  messages?: Partial<NxSortableMessages>;
  /** Commit a move: the item at `from` should end up at `to`. */
  onReorder: (from: number, to: number) => void;
  /** Where announcements go. Default the kit's shared live region. */
  announce?: (text: string) => void;
}

export interface NxSortable {
  /** The item being dragged or keyboard-lifted, `null` when idle. */
  draggingIndex: Readonly<Ref<number | null>>;
  /** Where it would land. */
  targetIndex: Readonly<Ref<number | null>>;
  /** How the current move is driven. */
  mode: Readonly<Ref<'pointer' | 'keyboard' | null>>;
  active: ComputedRef<boolean>;
  /** Spread onto each item (measuring hooks, drag state, pointer offset). */
  itemProps: (index: number) => Record<string, unknown>;
  /** Spread onto each item's grip button. */
  handleProps: (index: number) => Record<string, unknown>;
  /**
   * Only the pointer half of `handleProps`: the press handler plus the
   * touch-action/user-select style, and nothing else. For items that are no
   * grips — `handleProps` also sets `aria-label`, `aria-roledescription`,
   * `aria-pressed` and its own keydown, which would replace the item's role
   * and keys (a menu item that can be dragged). The caller then provides its
   * own keyboard path.
   */
  pointerProps: (index: number) => Record<string, unknown>;
  /** Side of the indicator on this item, or `null`. */
  dropSide: (index: number) => NxSortableDropSide | null;
  isDragging: (index: number) => boolean;
  /** Aborts a move in progress. */
  cancel: () => void;
}

/** Pointer travel (px) before a press on the grip becomes a drag. */
const DRAG_THRESHOLD = 4;
/** Auto-scroll zone at each edge of the scroll container (px). */
const EDGE = 48;
/** Top auto-scroll speed (px per frame). */
const MAX_SPEED = 18;

let seq = 0;

/**
 * Reorder a list by dragging a grip (pointer events — mouse, pen and touch
 * alike) or by keyboard: Space/Enter lifts the item, the arrow keys (and
 * Home/End) choose the slot, Space/Enter drops, Escape cancels; Alt+arrow
 * moves one step at once. Every step is announced. Near the scroll
 * container's edges the list auto-scrolls while dragging.
 *
 * The composable only reports `onReorder(from, to)`; the caller owns the array
 * (use `reorderItems`). Items render in place while dragging — the dragged one
 * follows the pointer and an indicator marks the landing slot.
 */
export function useNxSortable<T>(options: NxSortableOptions<T>): NxSortable {
  const id = `nx-sortable-${++seq}`;
  const axis = options.axis ?? 'vertical';
  const messages = useNxSortableMessages(options.messages);
  const announce = options.announce ?? ((text: string) => nxAnnounce(text));
  const list = computed(() => toValue(options.items));

  const draggingIndex = ref<number | null>(null);
  const targetIndex = ref<number | null>(null);
  const mode = ref<'pointer' | 'keyboard' | null>(null);
  const offset = ref(0);

  const labelAt = (index: number): string => {
    const item = list.value[index];
    return item !== undefined && options.labelOf
      ? options.labelOf(item, index)
      : `Eintrag ${index + 1}`;
  };
  const count = () => list.value.length;
  const say = (message: keyof Omit<NxSortableMessages, 'handle'>, label: string, index: number) =>
    announce(messages[message](label, index + 1, count()));

  // ---- pointer ----

  type Press = {
    index: number;
    pointerId: number | undefined;
    start: number;
    cross: number;
    scrollStart: number;
    pointer: number;
    started: boolean;
  };
  let press: Press | null = null;
  let frame: number | null = null;
  let scrollEl: HTMLElement | null = null;

  const along = (event: { clientX: number; clientY: number }) =>
    axis === 'vertical' ? event.clientY : event.clientX;
  const across = (event: { clientX: number; clientY: number }) =>
    axis === 'vertical' ? event.clientX : event.clientY;

  function items(): HTMLElement[] {
    const root = options.container.value;
    if (!root) return [];
    return [...root.querySelectorAll<HTMLElement>(`[data-nx-sortable-item="${id}"]`)].sort(
      (a, b) => Number(a.dataset.nxSortableIndex) - Number(b.dataset.nxSortableIndex)
    );
  }

  function spans(): NxSortableSpan[] {
    return items().map((el) => {
      const rect = el.getBoundingClientRect();
      return axis === 'vertical'
        ? { start: rect.top, end: rect.bottom }
        : { start: rect.left, end: rect.right };
    });
  }

  function isScrollable(el: HTMLElement): boolean {
    const style = getComputedStyle(el);
    const overflow = axis === 'vertical' ? style.overflowY : style.overflowX;
    const room =
      axis === 'vertical' ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth;
    return room && (overflow === 'auto' || overflow === 'scroll');
  }

  function resolveScrollEl(): HTMLElement | null {
    const explicit = options.scrollContainer?.value;
    if (explicit) return explicit;
    for (let el = options.container.value ?? null; el; el = el.parentElement) {
      if (isScrollable(el)) return el;
    }
    return (document.scrollingElement as HTMLElement | null) ?? null;
  }

  const scrollPos = () =>
    scrollEl ? (axis === 'vertical' ? scrollEl.scrollTop : scrollEl.scrollLeft) : 0;

  function track(): void {
    if (!press?.started) return;
    offset.value = press.pointer - press.start + (scrollPos() - press.scrollStart);
    targetIndex.value = clampIndex(
      sortableTargetIndex(spans(), press.index, press.pointer),
      count()
    );
  }

  function autoScroll(): void {
    frame = null;
    if (!press?.started || !scrollEl) return;
    const isPage = scrollEl === document.scrollingElement;
    const rect = isPage
      ? { start: 0, end: axis === 'vertical' ? window.innerHeight : window.innerWidth }
      : (() => {
          const r = scrollEl.getBoundingClientRect();
          return axis === 'vertical'
            ? { start: r.top, end: r.bottom }
            : { start: r.left, end: r.right };
        })();
    let speed = 0;
    if (press.pointer < rect.start + EDGE) {
      speed = -Math.ceil(((rect.start + EDGE - press.pointer) / EDGE) * MAX_SPEED);
    } else if (press.pointer > rect.end - EDGE) {
      speed = Math.ceil(((press.pointer - (rect.end - EDGE)) / EDGE) * MAX_SPEED);
    }
    if (speed !== 0) {
      const before = scrollPos();
      if (axis === 'vertical') scrollEl.scrollTop += speed;
      else scrollEl.scrollLeft += speed;
      if (scrollPos() !== before) track();
    }
    frame = requestAnimationFrame(autoScroll);
  }

  function swallowClick(event: MouseEvent): void {
    // The release after a drag must not also "click" the grip or the item.
    event.stopPropagation();
    event.preventDefault();
  }

  function onPointerMove(event: PointerEvent): void {
    if (
      !press ||
      (press.pointerId !== undefined &&
        event.pointerId !== undefined &&
        event.pointerId !== press.pointerId)
    )
      return;
    press.pointer = along(event);
    if (!press.started) {
      const travel = Math.hypot(press.pointer - press.start, across(event) - press.cross);
      if (travel < DRAG_THRESHOLD) return;
      press.started = true;
      mode.value = 'pointer';
      draggingIndex.value = press.index;
      targetIndex.value = press.index;
      scrollEl = resolveScrollEl();
      press.scrollStart = scrollPos();
      window.getSelection?.()?.removeAllRanges();
      if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(autoScroll);
    }
    event.preventDefault();
    track();
  }

  function onPointerUp(): void {
    const current = press;
    const from = draggingIndex.value;
    const to = targetIndex.value;
    teardownPointer();
    if (!current?.started || from === null || to === null) return;
    window.addEventListener('click', swallowClick, { capture: true, once: true });
    // A click that never comes (released outside the item) must not eat the next one.
    setTimeout(() => window.removeEventListener('click', swallowClick, { capture: true }), 0);
    finish(from, to, false);
  }

  function onPointerKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !press?.started) return;
    event.preventDefault();
    event.stopPropagation();
    const from = draggingIndex.value ?? 0;
    teardownPointer();
    resetState();
    say('cancelled', labelAt(from), from);
  }

  function teardownPointer(): void {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
    window.removeEventListener('keydown', onPointerKey, true);
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    press = null;
    scrollEl = null;
  }

  function onPointerCancel(): void {
    const started = press?.started;
    teardownPointer();
    if (started) resetState();
  }

  function onPointerDown(event: PointerEvent, index: number): void {
    if (toValue(options.disabled) || mode.value === 'keyboard') return;
    if (event.button !== undefined && event.button !== 0) return;
    teardownPointer();
    press = {
      index,
      pointerId: event.pointerId,
      start: along(event),
      cross: across(event),
      scrollStart: 0,
      pointer: along(event),
      started: false,
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('keydown', onPointerKey, true);
  }

  // ---- keyboard ----

  function focusHandle(index: number): void {
    void nextTick(() =>
      options.container.value
        ?.querySelector<HTMLElement>(
          `[data-nx-sortable-handle="${id}"][data-nx-sortable-index="${index}"]`
        )
        ?.focus()
    );
  }

  function stepOf(event: KeyboardEvent): number {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        return -1;
      case 'ArrowDown':
      case 'ArrowRight':
        return 1;
      default:
        return 0;
    }
  }

  function onHandleKeydown(event: KeyboardEvent, index: number): void {
    if (toValue(options.disabled) || mode.value === 'pointer') return;
    const lifted = mode.value === 'keyboard' && draggingIndex.value === index;
    const isToggle = event.key === ' ' || event.key === 'Enter';
    const step = stepOf(event);

    if (!lifted) {
      if (isToggle) {
        event.preventDefault();
        mode.value = 'keyboard';
        draggingIndex.value = index;
        targetIndex.value = index;
        say('lifted', labelAt(index), index);
      } else if (event.altKey && step !== 0) {
        event.preventDefault();
        const to = clampIndex(index + step, count());
        if (to !== index) finish(index, to, true);
      }
      return;
    }

    const target = targetIndex.value ?? index;
    let next = target;
    if (step !== 0) next = clampIndex(target + step, count());
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count() - 1;
    else if (isToggle) {
      event.preventDefault();
      finish(index, target, true);
      return;
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Keep a surrounding dialog open: this Escape belonged to the move.
      event.stopPropagation();
      resetState();
      say('cancelled', labelAt(index), index);
      return;
    } else if (event.key === 'Tab') {
      resetState();
      return;
    } else {
      return;
    }
    event.preventDefault();
    if (next !== target) {
      targetIndex.value = next;
      say('moved', labelAt(index), next);
    }
  }

  function onHandleBlur(index: number): void {
    if (mode.value === 'keyboard' && draggingIndex.value === index) resetState();
  }

  // ---- shared ----

  function resetState(): void {
    mode.value = null;
    draggingIndex.value = null;
    targetIndex.value = null;
    offset.value = 0;
  }

  function finish(from: number, to: number, refocus: boolean): void {
    const label = labelAt(from);
    resetState();
    if (from !== to) options.onReorder(from, to);
    say('dropped', label, to);
    if (refocus) focusHandle(to);
  }

  function cancel(): void {
    teardownPointer();
    resetState();
  }

  if (getCurrentScope()) onScopeDispose(cancel);

  function dropSide(index: number): NxSortableDropSide | null {
    const from = draggingIndex.value;
    const to = targetIndex.value;
    if (from === null || to === null || to !== index) return null;
    return sortableDropSide(from, to);
  }

  const isDragging = (index: number) => draggingIndex.value === index;

  function itemProps(index: number): Record<string, unknown> {
    const dragging = isDragging(index);
    const side = dropSide(index);
    const shift = dragging && mode.value === 'pointer' ? offset.value : 0;
    return {
      'data-nx-sortable-item': id,
      'data-nx-sortable-index': index,
      'data-nx-sortable-state': dragging ? mode.value : undefined,
      'data-nx-drop-side': side ?? undefined,
      style: shift
        ? {
            transform:
              axis === 'vertical'
                ? `translate3d(0, ${shift}px, 0)`
                : `translate3d(${shift}px, 0, 0)`,
            zIndex: 1,
            position: 'relative',
          }
        : undefined,
    };
  }

  function pointerProps(index: number): Record<string, unknown> {
    return {
      // Touch must not pan the page while the item is held, and a drag must
      // not select the text it passes over.
      style: { touchAction: 'none', userSelect: 'none' },
      onPointerdown: (event: PointerEvent) => onPointerDown(event, index),
      // A link or image under the pointer starts the browser's own drag, which
      // ends the pointer stream with `pointercancel`. Chromium starts it on a
      // link even with `draggable="false"`.
      onDragstart: (event: DragEvent) => event.preventDefault(),
    };
  }

  function handleProps(index: number): Record<string, unknown> {
    const disabled = toValue(options.disabled) === true;
    return {
      'data-nx-sortable-handle': id,
      'data-nx-sortable-index': index,
      'aria-label': messages.handle(labelAt(index)),
      'aria-roledescription': messages.grip(),
      'aria-pressed': mode.value === 'keyboard' && isDragging(index) ? 'true' : 'false',
      'aria-disabled': disabled ? 'true' : undefined,
      'aria-keyshortcuts':
        axis === 'vertical'
          ? 'Space Alt+ArrowUp Alt+ArrowDown'
          : 'Space Alt+ArrowLeft Alt+ArrowRight',
      ...pointerProps(index),
      onKeydown: (event: KeyboardEvent) => onHandleKeydown(event, index),
      onBlur: () => onHandleBlur(index),
    };
  }

  return {
    draggingIndex,
    targetIndex,
    mode,
    active: computed(() => mode.value !== null),
    itemProps,
    handleProps,
    pointerProps,
    dropSide,
    isDragging,
    cancel,
  };
}
